import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js'

import { ERROR_MESSAGES } from './constants'

/** Keep PostgREST filter URLs small and avoid single huge delete payloads. */
export const RESUME_DELETE_BATCH_SIZE = 10

type DeleteErrorLike = Pick<PostgrestError, 'code' | 'message' | 'details'> & {
  status?: number
}

export function formatDeleteResumesError(error: DeleteErrorLike): string {
  const code = error.code ?? ''
  const status = error.status
  const message = error.message?.trim()

  if (
    code === '23503' ||
    status === 409 ||
    message?.toLowerCase().includes('foreign key')
  ) {
    return 'Could not delete these resumes because they are still linked to optimization or billing records. Refresh and try again; if it persists, contact support.'
  }

  return message || 'Failed to delete resume(s)'
}

export function toDeleteResumesError(error: unknown): Error {
  if (error instanceof Error && !('code' in error)) {
    return error
  }
  if (error && typeof error === 'object' && 'message' in error) {
    return new Error(formatDeleteResumesError(error as DeleteErrorLike))
  }
  return new Error('Failed to delete resume(s)')
}

function chunkIds(ids: string[], size: number): string[][] {
  const chunks: string[][] = []
  for (let i = 0; i < ids.length; i += size) {
    chunks.push(ids.slice(i, i + size))
  }
  return chunks
}

export async function deleteResumes(
  supabase: SupabaseClient,
  params: { resumeIds: string[]; userId: string },
): Promise<void> {
  const uniqueIds = [...new Set(params.resumeIds.filter(Boolean))]
  if (uniqueIds.length === 0) return

  for (const batch of chunkIds(uniqueIds, RESUME_DELETE_BATCH_SIZE)) {
    const { error } = await supabase
      .from('resumes')
      .delete()
      .in('id', batch)
      .eq('user_id', params.userId)

    if (error) {
      throw toDeleteResumesError(error)
    }
  }
}

export async function deleteResumesForCurrentUser(
  supabase: SupabaseClient,
  resumeIds: string[],
): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) {
    throw new Error(ERROR_MESSAGES.MUST_BE_LOGGED_IN_TO_DELETE_RESUME)
  }
  await deleteResumes(supabase, { resumeIds, userId: user.id })
}
